// @ts-nocheck
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, MapPin, Briefcase, Calendar, User, Cross, MapPinned, ExternalLink, Search, X, SlidersHorizontal, Users, Baby, ZoomIn, ZoomOut, RotateCcw, Download, Image, ChevronDown, ChevronUp, Expand, Shrink, Building2, Globe, Instagram, Facebook, MessageCircle, ArrowLeft } from "lucide-react";
import defaultBusiness from "@/assets/default-business.png";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import defaultMale from "@/assets/default-male.png";
import defaultFemale from "@/assets/default-female.png";
import { toPng } from "html-to-image";

interface FamilyMember {
  id: string;
  name: string;
  photo_url: string | null;
  phone?: string | null;
  email?: string | null;
  birth_date?: string | null;
  location?: string | null;
  work?: string | null;
  bio?: string | null;
  gender?: string | null;
  is_deceased?: boolean;
  marital_status?: string | null;
  grave_number?: string | null;
  cemetery?: string | null;
  grave_coordinates?: string | null;
  cemetery_coordinates?: string | null;
  death_date?: string | null;
  member_code?: string | null;
}
interface Relationship {
  parent_id: string;
  child_id: string;
  relationship_type?: string;
}

interface SpouseBranch {
  spouse: FamilyMember;
  children: TreeNode[];
}

interface TreeNode {
  member: FamilyMember;
  spouses: FamilyMember[];
  children: TreeNode[];
  spouseBranches: SpouseBranch[];
}

function buildTree(members: FamilyMember[], relationships: Relationship[]): TreeNode[] {
  const parentRels = relationships.filter(r => r.relationship_type !== "spouse");
  const spouseRels = relationships.filter(r => r.relationship_type === "spouse");
  const childIds = new Set(parentRels.map((r) => r.child_id));
  const spouseIds = new Set<string>();
  spouseRels.forEach(r => { spouseIds.add(r.child_id); spouseIds.add(r.parent_id); });
  const parentIds = new Set(parentRels.map(r => r.parent_id));

  // Build a map of spouse pairs for quick lookup
  function getSpouseIds(memberId: string): string[] {
    const ids: string[] = [];
    spouseRels.forEach(r => {
      if (r.parent_id === memberId) ids.push(r.child_id);
      if (r.child_id === memberId) ids.push(r.parent_id);
    });
    return ids;
  }

  // A member who is a spouse of someone in childIds should NOT be a root
  // (they belong inside the tree as a spouse node)
  const spouseOfTreeMember = new Set<string>();
  spouseRels.forEach(r => {
    if (childIds.has(r.parent_id)) spouseOfTreeMember.add(r.child_id);
    if (childIds.has(r.child_id)) spouseOfTreeMember.add(r.parent_id);
  });

  const roots = members.filter((m) => 
    !childIds.has(m.id) && 
    !spouseOfTreeMember.has(m.id) &&
    (!spouseIds.has(m.id) || parentIds.has(m.id))
  );

  function getSpouses(memberId: string): FamilyMember[] {
    const sids = getSpouseIds(memberId);
    return sids.map(sid => members.find(m => m.id === sid)).filter(Boolean) as FamilyMember[];
  }

  // Build a set of all members who appear as a spouse of someone else
  const allSpouseMembers = new Set<string>();
  spouseRels.forEach(r => {
    // The child_id in a spouse relationship is the spouse
    allSpouseMembers.add(r.child_id);
  });

  function getChildrenForParent(parentId: string, spouseIdsList: string[]): TreeNode[] {
    const childRelsList = parentRels.filter((r) => 
      r.parent_id === parentId || spouseIdsList.includes(r.parent_id)
    );
    const seen = new Set<string>();
    const uniqueRels = childRelsList.filter(r => {
      if (seen.has(r.child_id)) return false;
      seen.add(r.child_id);
      return true;
    });
    return uniqueRels
      .map((rel) => {
        const member = members.find((m) => m.id === rel.child_id);
        if (!member) return null;
        const isChildOfParent = parentRels.some(r => r.parent_id === parentId && r.child_id === member.id);
        const isChildOfSpouse = spouseIdsList.some(sid => parentRels.some(r => r.parent_id === sid && r.child_id === member.id));
        if (allSpouseMembers.has(member.id) && !isChildOfParent && !isChildOfSpouse) return null;
        return buildNode(member);
      })
      .filter(Boolean)
      .sort((a, b) => {
        const dateA = (a as TreeNode).member.birth_date;
        const dateB = (b as TreeNode).member.birth_date;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      }) as TreeNode[];
  }

  // Get children specifically linked to a particular spouse (parent)
  function getChildrenOfSpouse(spouseId: string): TreeNode[] {
    const childRelsList = parentRels.filter(r => r.parent_id === spouseId);
    const seen = new Set<string>();
    const uniqueRels = childRelsList.filter(r => {
      if (seen.has(r.child_id)) return false;
      seen.add(r.child_id);
      return true;
    });
    return uniqueRels
      .map((rel) => {
        const member = members.find((m) => m.id === rel.child_id);
        if (!member) return null;
        if (allSpouseMembers.has(member.id)) {
          const isChild = parentRels.some(r => r.parent_id === spouseId && r.child_id === member.id);
          if (!isChild) return null;
        }
        return buildNode(member);
      })
      .filter(Boolean)
      .sort((a, b) => {
        const dateA = (a as TreeNode).member.birth_date;
        const dateB = (b as TreeNode).member.birth_date;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      }) as TreeNode[];
  }

  function buildNode(m: FamilyMember): TreeNode {
    const memberSpouses = getSpouses(m.id);
    const spouseIdsList = getSpouseIds(m.id);
    
    if (memberSpouses.length > 1) {
      // Multiple spouses: group children by spouse
      const spouseBranches: SpouseBranch[] = memberSpouses.map(spouse => ({
        spouse,
        children: getChildrenOfSpouse(spouse.id),
      }));
      // Children directly under the member (not through any spouse)
      const spouseChildIds = new Set(spouseBranches.flatMap(b => b.children.map(c => c.member.id)));
      const directChildren = getChildrenForParent(m.id, []).filter(c => !spouseChildIds.has(c.member.id));
      return { member: m, spouses: memberSpouses, children: directChildren, spouseBranches };
    } else {
      // Single or no spouse: all children flat
      const children = getChildrenForParent(m.id, spouseIdsList);
      return { member: m, spouses: memberSpouses, children, spouseBranches: [] };
    }
  }

  return roots.map((m) => buildNode(m));
}

function calculateAge(birthDate: string): number {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function countDescendants(node: TreeNode): { children: number; grandchildren: number } {
  const directChildren = node.children.length;
  let grandchildren = 0;
  for (const child of node.children) {
    grandchildren += child.children.length;
    // Also count deeper descendants as grandchildren
    const deeper = countDescendants(child);
    grandchildren += deeper.grandchildren;
  }
  return { children: directChildren, grandchildren };
}

function MemberCard({ member, isAuthenticated, onClick, size = "normal", highlighted = false, memberNumber }: { member: FamilyMember; isAuthenticated: boolean; onClick: () => void; size?: "normal" | "large"; highlighted?: boolean; memberNumber?: number }) {
  const isLarge = size === "large";
  const avatarSize = isLarge ? "h-14 w-14 sm:h-18 sm:w-18 md:h-24 md:w-24" : "h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16";
  const glowSize = isLarge ? "-inset-2 sm:-inset-3" : "-inset-1.5";
  const textSize = isLarge ? "text-[10px] sm:text-sm md:text-base" : "text-[9px] sm:text-xs md:text-sm";
  const defaultAvatar = member.gender === "female" ? defaultFemale : defaultMale;
  const avatarSrc = isAuthenticated ? (member.photo_url || defaultAvatar) : defaultAvatar;
  const isFemaleDefault = (!member.photo_url || !isAuthenticated) && member.gender === "female";
  const imgScale = isFemaleDefault ? "scale-[0.92]" : "scale-[0.78]";
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }, [highlighted]);
  
  return (
    <motion.div
      ref={cardRef}
      data-testid={`tree-member-${member.id}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: highlighted ? 1.12 : 1 }}
      whileHover={{ scale: 1.06, y: -3 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 350, damping: 25 }}
      onClick={isAuthenticated ? onClick : undefined}
      className={`flex flex-col items-center group ${isAuthenticated ? "cursor-pointer" : ""} ${highlighted ? "z-10" : ""}`}
    >
      <div className="relative">
        {/* Glow Effect for Highlighted */}
        {highlighted && (
          <div className="absolute -inset-4 rounded-full animate-pulse"
            style={{ background: "radial-gradient(circle, hsla(43,80%,50%,0.6), transparent 70%)" }}
          />
        )}
        {/* Hover Glow */}
        <div className={`absolute ${glowSize} rounded-full ${highlighted ? 'opacity-90' : 'opacity-0'} group-hover:opacity-70 transition-opacity duration-500`}
          style={{ background: "radial-gradient(circle, hsla(43,80%,50%,0.5), transparent 70%)" }}
        />
        {/* Border Ring */}
        <div className={`absolute -inset-[3px] sm:-inset-[4px] rounded-full ${member.is_deceased ? 'bg-gradient-to-br from-muted-foreground/40 to-muted-foreground/20' : 'bg-gradient-to-br from-primary via-primary/80 to-primary/60'} ${highlighted ? 'opacity-100' : 'opacity-70 group-hover:opacity-90'} transition-opacity duration-300`} />
        {/* Avatar */}
        <Avatar className={`${avatarSize} relative ${member.is_deceased ? 'grayscale opacity-80' : ''} ring-2 ring-background shadow-xl overflow-hidden`}>
          <AvatarImage src={avatarSrc} alt={member.name} className={`object-contain ${imgScale} rounded-full bg-secondary/50`} />
          <AvatarFallback className="bg-gradient-to-br from-secondary to-secondary/50">
            <img src={defaultAvatar} alt={member.name} className={`w-full h-full object-contain ${imgScale}`} />
          </AvatarFallback>
        </Avatar>
        {/* Deceased Badge */}
        {member.is_deceased && (
          <div className="absolute -bottom-1 -right-1 bg-gradient-to-br from-background to-muted border-2 border-muted-foreground/30 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shadow-md">
            <span className="text-[8px] sm:text-[10px]">🕊️</span>
          </div>
        )}
        {/* Member Number Badge */}
        {memberNumber != null && (
          <div className="absolute -top-1.5 -left-1.5 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-full min-w-[18px] h-[18px] sm:min-w-[22px] sm:h-[22px] flex items-center justify-center text-[8px] sm:text-[10px] font-bold shadow-lg border border-primary-foreground/20">
            {memberNumber}
          </div>
        )}
      </div>
      {/* Name */}
      <div className="mt-1.5 sm:mt-2 md:mt-3 text-center max-w-[65px] sm:max-w-[85px] md:max-w-[110px]">
        <p className={`font-amiri font-bold ${highlighted ? 'text-primary' : 'text-foreground group-hover:text-primary/90'} ${textSize} leading-tight transition-colors duration-300`}>
          {member.name}
        </p>
      </div>
    </motion.div>
  );
}

function CoupleBlock({ node, isAuthenticated, onSelect, highlightedId, numberMap }: { node: TreeNode; isAuthenticated: boolean; onSelect: (m: FamilyMember) => void; highlightedId?: string | null; numberMap?: Map<string, number> }) {
  const content = (
    <div className="flex items-center gap-0.5">
      <MemberCard member={node.member} isAuthenticated={isAuthenticated} onClick={() => onSelect(node.member)} size="large" highlighted={highlightedId === node.member.id} memberNumber={numberMap?.get(node.member.id)} />
      {node.spouses.map((spouse, idx) => (
        <div key={spouse.id} className="flex items-center">
          <div className="flex items-center mx-0.5">
            <div className="w-1.5 sm:w-3 h-[1px] sm:h-[1.5px] bg-gradient-to-r from-primary/50 to-primary/20" />
            <div className="w-1 h-1 rounded-full border border-primary/40 bg-background" />
            <div className="w-1.5 sm:w-3 h-[1px] sm:h-[1.5px] bg-gradient-to-l from-primary/50 to-primary/20" />
          </div>
          <MemberCard member={spouse} isAuthenticated={isAuthenticated} onClick={() => onSelect(spouse)} size="large" highlighted={highlightedId === spouse.id} memberNumber={numberMap?.get(spouse.id)} />
        </div>
      ))}
    </div>
  );

  if (node.spouses.length > 0) {
    return (
      <div className="relative">
        <div className="absolute -inset-1 sm:-inset-2 md:-inset-3 rounded-xl sm:rounded-2xl bg-primary/[0.03] border border-primary/10 -z-10" />
        {content}
      </div>
    );
  }

  return content;
}

function TreeNodeComponent({ node, isAuthenticated, onSelect, depth = 0, highlightedId, numberMap, expandedNodes, onToggleExpand }: { node: TreeNode; isAuthenticated: boolean; onSelect: (m: FamilyMember) => void; depth?: number; highlightedId?: string | null; numberMap?: Map<string, number>; expandedNodes: Set<string>; onToggleExpand: (id: string) => void }) {
  const isRoot = depth === 0;
  const hasMultipleSpouses = node.spouseBranches.length > 0;
  const hasChildren = node.children.length > 0 || hasMultipleSpouses;
  const isExpanded = expandedNodes.has(node.member.id);

  // For multi-spouse: render husband on top, then branches for each wife below
  if (hasMultipleSpouses) {
    return (
      <div className="flex flex-col items-center">
        {/* Husband card (no spouse beside him) */}
        {isRoot ? (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <MemberCard member={node.member} isAuthenticated={isAuthenticated} onClick={() => onSelect(node.member)} size="large" highlighted={highlightedId === node.member.id} memberNumber={numberMap?.get(node.member.id)} />
          </motion.div>
        ) : (
          <MemberCard member={node.member} isAuthenticated={isAuthenticated} onClick={() => onSelect(node.member)} size="large" highlighted={highlightedId === node.member.id} memberNumber={numberMap?.get(node.member.id)} />
        )}

        {/* Expand/Collapse */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => { e.stopPropagation(); onToggleExpand(node.member.id); }}
          className="mt-0.5 sm:mt-1 flex flex-col items-center group/expand"
        >
          <div className="w-[1px] sm:w-[1.5px] h-2 sm:h-3 bg-gradient-to-b from-primary/40 to-primary/20" />
          <div className={`w-5 h-5 sm:w-7 sm:h-7 rounded-full border-[1.5px] sm:border-2 border-primary/30 bg-background flex items-center justify-center transition-all duration-300 hover:border-primary/60 hover:bg-primary/10 ${isExpanded ? 'bg-primary/10 border-primary/50' : ''}`}>
            <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
              <ChevronDown className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-primary/70" />
            </motion.div>
          </div>
          <span className="text-[7px] sm:text-[9px] text-muted-foreground mt-0.5 opacity-70 sm:opacity-0 sm:group-hover/expand:opacity-100 transition-opacity">
            {node.spouseBranches.reduce((sum, b) => sum + b.children.length, 0) + node.children.length}
          </span>
        </motion.button>

        {/* Wife branches */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0, scale: 0.95 }}
              animate={{ opacity: 1, height: "auto", scale: 1 }}
              exit={{ opacity: 0, height: 0, scale: 0.95 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="flex flex-col items-center overflow-hidden"
            >
              <div className="w-[1px] sm:w-[1.5px] h-4 sm:h-6 bg-gradient-to-b from-primary/30 to-primary/10" />
              <div className="relative flex items-start">
                {node.spouseBranches.length > 1 && (
                  <div className="absolute top-0 h-[2px] rounded-full"
                    style={{
                      left: `${100 / (node.spouseBranches.length * 2)}%`,
                      right: `${100 / (node.spouseBranches.length * 2)}%`,
                      background: "linear-gradient(90deg, transparent, hsl(43 80% 50% / 0.25), transparent)",
                    }}
                  />
                )}
                <div className="flex gap-6 sm:gap-10 md:gap-16">
                  {node.spouseBranches.map((branch, i) => (
                    <motion.div
                      key={branch.spouse.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.12, duration: 0.3 }}
                      className="flex flex-col items-center"
                    >
                      {node.spouseBranches.length > 1 && (
                        <div className="w-[1px] sm:w-[1.5px] h-3 sm:h-5 bg-primary/20" />
                      )}
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rotate-45 border border-primary/30 bg-primary/10 mb-0.5 sm:mb-1" />
                      
                      {/* Wife card with decorated border */}
                      <div className="relative">
                        <div className="absolute -inset-1 sm:-inset-2 md:-inset-3 rounded-xl sm:rounded-2xl bg-primary/[0.04] border border-primary/15 -z-10" />
                        <MemberCard member={branch.spouse} isAuthenticated={isAuthenticated} onClick={() => onSelect(branch.spouse)} size="large" highlighted={highlightedId === branch.spouse.id} memberNumber={numberMap?.get(branch.spouse.id)} />
                      </div>

                      {/* Children of this wife */}
                      {branch.children.length > 0 && (
                        <>
                          <div className="w-[1px] sm:w-[1.5px] h-3 sm:h-5 bg-gradient-to-b from-primary/30 to-primary/10 mt-1" />
                          <div className="relative flex items-start">
                            {branch.children.length > 1 && (
                              <div className="absolute top-0 h-[2px] rounded-full"
                                style={{
                                  left: `${100 / (branch.children.length * 2)}%`,
                                  right: `${100 / (branch.children.length * 2)}%`,
                                  background: "linear-gradient(90deg, transparent, hsl(43 80% 50% / 0.15), transparent)",
                                }}
                              />
                            )}
                            <div className="flex gap-3 sm:gap-5 md:gap-10">
                              {branch.children.map((child, ci) => (
                                <motion.div
                                  key={child.member.id}
                                  initial={{ opacity: 0, y: 15 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: ci * 0.08, duration: 0.3 }}
                                  className="flex flex-col items-center"
                                >
                                  {branch.children.length > 1 && (
                                    <div className="w-[1px] sm:w-[1.5px] h-3 sm:h-5 bg-primary/20" />
                                  )}
                                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rotate-45 border border-primary/30 bg-primary/10 mb-0.5 sm:mb-1" />
                                  <TreeNodeComponent node={child} isAuthenticated={isAuthenticated} onSelect={onSelect} depth={depth + 1} highlightedId={highlightedId} numberMap={numberMap} expandedNodes={expandedNodes} onToggleExpand={onToggleExpand} />
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Standard single/no spouse layout
  return (
    <div className="flex flex-col items-center">
      {isRoot ? (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <CoupleBlock node={node} isAuthenticated={isAuthenticated} onSelect={onSelect} highlightedId={highlightedId} numberMap={numberMap} />
        </motion.div>
      ) : (
        <CoupleBlock node={node} isAuthenticated={isAuthenticated} onSelect={onSelect} highlightedId={highlightedId} numberMap={numberMap} />
      )}

      {/* Expand/Collapse arrow */}
      {hasChildren && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => { e.stopPropagation(); onToggleExpand(node.member.id); }}
          className="mt-0.5 sm:mt-1 flex flex-col items-center group/expand"
        >
          <div className="w-[1px] sm:w-[1.5px] h-2 sm:h-3 bg-gradient-to-b from-primary/40 to-primary/20" />
          <div className={`w-5 h-5 sm:w-7 sm:h-7 rounded-full border-[1.5px] sm:border-2 border-primary/30 bg-background flex items-center justify-center transition-all duration-300 hover:border-primary/60 hover:bg-primary/10 ${isExpanded ? 'bg-primary/10 border-primary/50' : ''}`}>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-primary/70" />
            </motion.div>
          </div>
          <span className="text-[7px] sm:text-[9px] text-muted-foreground mt-0.5 opacity-70 sm:opacity-0 sm:group-hover/expand:opacity-100 transition-opacity">
            {node.children.length}
          </span>
        </motion.button>
      )}

      {/* Children */}
      <AnimatePresence>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0, scale: 0.95 }}
            animate={{ opacity: 1, height: "auto", scale: 1 }}
            exit={{ opacity: 0, height: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="flex flex-col items-center overflow-hidden"
          >
            <div className="w-[1px] sm:w-[1.5px] h-4 sm:h-6 bg-gradient-to-b from-primary/30 to-primary/10" />
            <div className="relative flex items-start">
              {node.children.length > 1 && (
                <div className="absolute top-0 h-[2px] rounded-full"
                  style={{
                    left: `${100 / (node.children.length * 2)}%`,
                    right: `${100 / (node.children.length * 2)}%`,
                    background: "linear-gradient(90deg, transparent, hsl(43 80% 50% / 0.25), transparent)",
                  }}
                />
              )}
              <div className="flex gap-3 sm:gap-5 md:gap-10">
                {node.children.map((child, i) => (
                  <motion.div
                    key={child.member.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.3 }}
                    className="flex flex-col items-center"
                  >
                    {node.children.length > 1 && (
                      <div className="w-[1px] sm:w-[1.5px] h-3 sm:h-5 bg-primary/20" />
                    )}
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rotate-45 border border-primary/30 bg-primary/10 mb-0.5 sm:mb-1" />
                    <TreeNodeComponent node={child} isAuthenticated={isAuthenticated} onSelect={onSelect} depth={depth + 1} highlightedId={highlightedId} numberMap={numberMap} expandedNodes={expandedNodes} onToggleExpand={onToggleExpand} />
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

type BusinessFull = { id: string; name_ar: string; name_en: string | null; logo_url: string | null; activity: string | null; location: string | null; country: string | null; emirate: string | null; area: string | null; street: string | null; phone: string | null; email: string | null; website: string | null; instagram: string | null; facebook: string | null; whatsapp: string | null; twitter_x: string | null };

function MemberDetail({ member, childrenCount, grandchildrenCount, memberNumber, memberCode, galleryPhotos, memberBusinesses, onViewBusiness }: { member: FamilyMember; childrenCount?: number; grandchildrenCount?: number; memberNumber?: number; memberCode?: string; galleryPhotos?: { id: string; photo_url: string; title: string | null }[]; memberBusinesses?: BusinessFull[]; onViewBusiness?: (biz: BusinessFull) => void }) {
  const maritalLabel = member.marital_status === "married" ? "متزوج/ة" :
    member.marital_status === "single" ? "أعزب/عزباء" :
    member.marital_status === "divorced" ? "مطلق/ة" :
    member.marital_status === "widowed" ? "أرمل/ة" : null;

  const phones = member.phone ? member.phone.split(" | ") : [];

  const details = [
    ...phones.map((p, i) => ({ icon: Phone, label: phones.length > 1 ? `هاتف ${i + 1}` : "الجوال", value: p })),
    { icon: Mail, label: "البريد", value: member.email },
    { icon: Calendar, label: "تاريخ الميلاد", value: member.birth_date ? `${member.birth_date} (${calculateAge(member.birth_date)} سنة)` : null },
    { icon: MapPin, label: "مكان الإقامة", value: member.location },
    { icon: Briefcase, label: "العمل", value: member.work },
  ].filter((d) => d.value);

  const deceasedDetails = member.is_deceased ? [
    { icon: Calendar, label: "تاريخ الوفاة", value: (member as any).death_date || null },
    { icon: Cross, label: "المقبرة", value: member.cemetery },
    { icon: MapPinned, label: "رقم القبر", value: member.grave_number },
  ].filter((d) => d.value) : [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="relative">
          <div className="absolute -inset-[3px] rounded-full gold-gradient opacity-60" />
          <Avatar className={`h-20 w-20 relative ${member.is_deceased ? 'grayscale' : ''} ring-2 ring-background`}>
            <AvatarImage src={member.photo_url || (member.gender === "female" ? defaultFemale : defaultMale)} className="object-cover" />
            <AvatarFallback className="bg-secondary">
              <img src={member.gender === "female" ? defaultFemale : defaultMale} alt={member.name} className="w-full h-full object-cover" />
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="flex-1">
          <h3 className="font-amiri text-2xl gold-text leading-tight">{member.name}</h3>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {memberNumber != null && <Badge className="text-[10px] px-2 gold-gradient text-primary-foreground">#{memberNumber}</Badge>}
            {memberCode && <Badge variant="outline" className="text-[10px] px-2 border-primary/30 font-mono tracking-wider">{memberCode}</Badge>}
            {member.is_deceased && <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive bg-destructive/5 px-2">متوفي 🕊️</Badge>}
            {maritalLabel && <Badge variant="secondary" className="text-[10px] px-2">{maritalLabel}</Badge>}
            <Badge variant="outline" className="text-[10px] px-2 border-primary/20">{member.gender === "female" ? "أنثى" : "ذكر"}</Badge>
          </div>
        </div>
      </div>

      {/* Descendants stats */}
      {(childrenCount != null && childrenCount > 0) && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[11px] px-2.5 py-1">{childrenCount} أبناء</Badge>
          {(grandchildrenCount != null && grandchildrenCount > 0) && (
            <Badge variant="outline" className="text-[11px] px-2.5 py-1 border-primary/20">{grandchildrenCount} أحفاد</Badge>
          )}
        </div>
      )}

      {/* Details */}
      {details.length > 0 && (
        <div className="space-y-2.5 pt-1">
          {details.map((d, i) => (
            <motion.div
              key={`${d.label}-${i}`}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-center gap-3 py-1.5 px-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
            >
              <div className="p-1.5 rounded-md bg-primary/10">
                <d.icon className="h-3.5 w-3.5 text-primary" />
              </div>
              <span className="text-muted-foreground text-xs min-w-[60px]">{d.label}</span>
              <span className="text-sm text-foreground">{d.value}</span>
            </motion.div>
          ))}
        </div>
      )}

      {/* Deceased info */}
      {deceasedDetails.length > 0 && (
        <div className="space-y-2.5 pt-3 border-t border-destructive/15">
          <p className="text-xs text-destructive font-medium flex items-center gap-1.5">
            <Cross className="h-3 w-3" /> معلومات الدفن
          </p>
          {deceasedDetails.map((d, i) => (
            <motion.div
              key={`${d.label}-${i}`}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-center gap-3 py-1.5 px-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-colors"
            >
              <div className="p-1.5 rounded-md bg-destructive/10">
                <d.icon className="h-3.5 w-3.5 text-destructive" />
              </div>
              <span className="text-muted-foreground text-xs min-w-[60px]">{d.label}</span>
              <span className="text-sm text-foreground flex-1" dir="ltr">{d.value}</span>
            </motion.div>
          ))}
          {/* Google Maps buttons */}
          <div className="flex gap-2 pt-1">
            {member.cemetery_coordinates && (
              <a
                href={member.cemetery_coordinates}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <MapPin className="h-3 w-3" />
                موقع المقبرة
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
            {member.grave_coordinates && (
              <a
                href={member.grave_coordinates}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <MapPinned className="h-3 w-3" />
                موقع القبر
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Bio */}
      {member.bio && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="pt-3 border-t border-primary/10"
        >
          <p className="text-muted-foreground text-sm leading-relaxed flex items-start gap-2">
            <div className="p-1.5 rounded-md bg-primary/10 mt-0.5 shrink-0">
              <User className="h-3.5 w-3.5 text-primary" />
            </div>
            {member.bio}
          </p>
        </motion.div>
      )}

      {/* Businesses */}
      {memberBusinesses && memberBusinesses.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="pt-3 border-t border-primary/10"
        >
          <p className="text-xs text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
            <Building2 className="h-3 w-3" /> شركات ({memberBusinesses.length})
          </p>
          <div className="space-y-2">
            {memberBusinesses.map((biz) => (
              <div key={biz.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-secondary/30">
                <img src={biz.logo_url || defaultBusiness} alt={biz.name_ar} className={`w-8 h-8 rounded-full border border-primary/10 ${biz.logo_url ? 'object-cover' : 'object-contain p-0.5'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{biz.name_ar}</p>
                  {biz.name_en && <p className="text-xs text-muted-foreground" dir="ltr">{biz.name_en}</p>}
                </div>
                <Button variant="outline" size="sm" className="rounded-lg border-primary/20 text-primary text-xs px-2 py-1 h-auto" onClick={() => onViewBusiness?.(biz)}>
                  زيارة ملف الشركة <ArrowLeft className="h-3 w-3 mr-1" />
                </Button>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Gallery photos */}
      {galleryPhotos && galleryPhotos.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="pt-3 border-t border-primary/10"
        >
          <p className="text-xs text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
            <Image className="h-3 w-3" /> صور ({galleryPhotos.length})
          </p>
          <div className="grid grid-cols-3 gap-2">
            {galleryPhotos.map((photo) => (
              <Dialog key={photo.id}>
                <DialogTrigger asChild>
                  <div className="rounded-lg overflow-hidden border border-primary/10 cursor-pointer hover:border-primary/30 transition-colors">
                    <img src={photo.photo_url} alt={photo.title || "صورة"} className="w-full h-20 object-cover" loading="lazy" />
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-3xl glass-card border-primary/20 rounded-2xl p-2 sm:p-4" dir="rtl">
                  <DialogHeader>
                    <DialogTitle className="font-amiri text-lg gold-text">{photo.title || "صورة"}</DialogTitle>
                  </DialogHeader>
                  <img src={photo.photo_url} alt={photo.title || "صورة"} className="w-full max-h-[75vh] object-contain rounded-xl" />
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

const FamilyTree = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [galleryPhotos, setGalleryPhotos] = useState<{ id: string; photo_url: string; title: string | null; member_id: string | null }[]>([]);
  const [memberBusinesses, setMemberBusinesses] = useState<Record<string, BusinessFull[]>>({});
  const [viewingBusiness, setViewingBusiness] = useState<BusinessFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<FamilyMember | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "alive" | "deceased">("all");
  const [genderFilter, setGenderFilter] = useState<"all" | "male" | "female">("all");
  const [maritalFilter, setMaritalFilter] = useState<"all" | "single" | "married" | "divorced" | "widowed">("all");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [zoom, setZoom] = useState(typeof window !== 'undefined' && window.innerWidth < 768 ? 0.7 : 1);
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const treeContentRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [authLoading, user, navigate]);

  // Drag to pan support
  const didDrag = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const el = treeContainerRef.current;
    if (!el || e.pointerType === 'touch') return;
    didDrag.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const el = treeContainerRef.current;
    if (!el) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (!isDragging && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      setIsDragging(true);
      didDrag.current = true;
    }
    if (isDragging) {
      el.scrollLeft = dragStart.current.scrollLeft - dx;
      el.scrollTop = dragStart.current.scrollTop - dy;
    }
  }, [isDragging]);

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
    dragStart.current = null;
  }, []);

  const handleDownloadPng = useCallback(async () => {
    const el = treeContentRef.current;
    if (!el) return;
    setIsDownloading(true);
    try {
      const dataUrl = await toPng(el, {
        backgroundColor: "#0c0a09",
        pixelRatio: 2,
        cacheBust: true,
      });
      const link = document.createElement("a");
      link.download = "family-tree.png";
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Failed to download tree:", err);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  // Pinch-to-zoom + touch drag support
  useEffect(() => {
    const el = treeContainerRef.current;
    if (!el) return;

    let initialDistance = 0;
    let initialZoom = 1;
    let touchDragStart: { x: number; y: number; scrollLeft: number; scrollTop: number } | null = null;

    const getDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        initialDistance = getDistance(e.touches);
        initialZoom = zoom;
        touchDragStart = null;
      } else if (e.touches.length === 1) {
        touchDragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = getDistance(e.touches);
        const scale = currentDistance / initialDistance;
        const newZoom = Math.min(Math.max(initialZoom * scale, 0.3), 2);
        setZoom(newZoom);
      } else if (e.touches.length === 1 && touchDragStart) {
        e.preventDefault();
        const dx = e.touches[0].clientX - touchDragStart.x;
        const dy = e.touches[0].clientY - touchDragStart.y;
        el.scrollLeft = touchDragStart.scrollLeft - dx;
        el.scrollTop = touchDragStart.scrollTop - dy;
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, [zoom]);

  useEffect(() => {
    const fetchData = async () => {
      const [membersRes, relsRes, galleryRes, bizOwnersRes] = await Promise.all([
        supabase.from("family_members").select("*"),
        supabase.from("family_relationships").select("parent_id, child_id, relationship_type"),
        supabase.from("gallery_photos").select("id, photo_url, title, member_id").order("created_at", { ascending: false }),
        supabase.from("business_owners").select("member_id, family_businesses(id, name_ar, name_en, logo_url, activity, location, country, emirate, area, street, phone, email, website, instagram, facebook, whatsapp, twitter_x)"),
      ]);
      const rawMembers = (membersRes.data || []) as unknown as FamilyMember[];
      if (!user) {
        setMembers(rawMembers.map(m => ({ id: m.id, name: m.name, photo_url: m.photo_url, gender: m.gender, is_deceased: m.is_deceased })) as FamilyMember[]);
      } else {
        setMembers(rawMembers);
      }
      setRelationships((relsRes.data || []) as unknown as Relationship[]);
      setGalleryPhotos((galleryRes.data || []) as any[]);
      // Map member businesses
      const bizMap: Record<string, BusinessFull[]> = {};
      (bizOwnersRes.data || []).forEach((row: any) => {
        if (row.family_businesses) {
          if (!bizMap[row.member_id]) bizMap[row.member_id] = [];
          bizMap[row.member_id].push(row.family_businesses);
        }
      });
      setMemberBusinesses(bizMap);
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const filteredMembers = members.filter(m => {
    if (filter === "alive" && m.is_deceased) return false;
    if (filter === "deceased" && !m.is_deceased) return false;
    if (genderFilter !== "all" && m.gender !== genderFilter) return false;
    if (maritalFilter !== "all" && m.marital_status !== maritalFilter) return false;
    return true;
  });
  const tree = buildTree(filteredMembers, relationships);

  const memberNumberMap = useMemo(() => {
    const sorted = [...members].sort((a, b) => {
      const dateA = a.birth_date;
      const dateB = b.birth_date;
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
    const map = new Map<string, number>();
    sorted.forEach((m, i) => map.set(m.id, i + 1));
    return map;
  }, [members]);

  // Use DB member_code
  const memberCodeMap = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((m) => {
      if (m.member_code) map.set(m.id, m.member_code);
    });
    return map;
  }, [members]);

  // Collect all node IDs for expand all
  const allNodeIds = useMemo(() => {
    const ids = new Set<string>();
    function collect(nodes: TreeNode[]) {
      for (const n of nodes) {
        if (n.children.length > 0) ids.add(n.member.id);
        collect(n.children);
      }
    }
    collect(tree);
    return ids;
  }, [tree]);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedNodes(new Set(allNodeIds));
    setAllExpanded(true);
  }, [allNodeIds]);

  const handleCollapseAll = useCallback(() => {
    setExpandedNodes(new Set());
    setAllExpanded(false);
  }, []);

  // Root ancestor stats
  const rootStats = useMemo(() => {
    if (tree.length === 0) return null;
    const root = tree[0];
    const stats = countDescendants(root);
    return { name: root.member.name, ...stats };
  }, [tree]);

  // Find node counts for selected member
  function findNodeCounts(nodes: TreeNode[], memberId: string): { children: number; grandchildren: number } | null {
    for (const node of nodes) {
      if (node.member.id === memberId) return countDescendants(node);
      const found = findNodeCounts(node.children, memberId);
      if (found) return found;
    }
    return null;
  }
  const selectedCounts = selected ? findNodeCounts(tree, selected.id) : null;

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-8 md:pb-12 px-1 md:px-4">
      <div className="container mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6 sm:mb-12">
          <div className="inline-block relative">
            <h1 className="font-amiri text-2xl sm:text-4xl md:text-5xl font-bold gold-text mb-2 sm:mb-3">شجرة العائلة</h1>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-20 sm:w-32 h-[1px]"
              style={{ background: "linear-gradient(90deg, transparent, hsl(43 80% 50% / 0.6), transparent)" }}
            />
          </div>
          <p className="text-muted-foreground text-sm mt-4">
            {user ? "اضغط على أي فرد لعرض التفاصيل الكاملة" : "وضع الزائر — الاسم والصورة فقط"}
          </p>
          {/* Root ancestor stats */}
          {rootStats && (
            <div className="flex justify-center gap-2 sm:gap-4 mt-4 sm:mt-6">
              <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-xl bg-primary/5 border border-primary/15">
                <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
                <div className="text-right">
                  <p className="text-lg sm:text-2xl font-bold text-primary">{rootStats.children}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">أبناء</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-xl bg-primary/5 border border-primary/15">
                <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
                  <Baby className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
                <div className="text-right">
                  <p className="text-lg sm:text-2xl font-bold text-primary">{rootStats.grandchildren}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">أحفاد</p>
                </div>
              </div>
            </div>
          )}

          {/* Search bar */}
          <div className="relative max-w-xs mx-auto mt-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث بالاسم..."
              className="pr-9 pl-9 text-right"
              dir="rtl"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filter toggle */}
          <div className="flex justify-center mt-4">
            <Badge
              variant={showFilters ? "default" : "outline"}
              className="cursor-pointer text-xs px-4 py-1.5 gap-1.5 transition-colors"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="h-3 w-3" />
              فلتر
            </Badge>
          </div>

          {/* Collapsible filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="flex justify-center gap-2 mt-3">
                  {([
                    { value: "all" as const, label: "الكل" },
                    { value: "alive" as const, label: "الأحياء" },
                    { value: "deceased" as const, label: "المتوفين 🕊️" },
                  ]).map(f => (
                    <Badge key={f.value} variant={filter === f.value ? "default" : "outline"} className={`cursor-pointer text-xs px-3 py-1.5 transition-colors ${filter === f.value ? '' : 'hover:bg-secondary/60'}`} onClick={() => setFilter(f.value)}>{f.label}</Badge>
                  ))}
                </div>
                <div className="flex justify-center gap-2 mt-2">
                  {([
                    { value: "all" as const, label: "الكل" },
                    { value: "male" as const, label: "ذكور" },
                    { value: "female" as const, label: "إناث" },
                  ]).map(f => (
                    <Badge key={f.value} variant={genderFilter === f.value ? "default" : "outline"} className={`cursor-pointer text-xs px-3 py-1.5 transition-colors ${genderFilter === f.value ? '' : 'hover:bg-secondary/60'}`} onClick={() => setGenderFilter(f.value)}>{f.label}</Badge>
                  ))}
                </div>
                <div className="flex justify-center gap-2 mt-2">
                  {([
                    { value: "all" as const, label: "الكل" },
                    { value: "single" as const, label: "أعزب/عزباء" },
                    { value: "married" as const, label: "متزوج/ة" },
                    { value: "divorced" as const, label: "مطلق/ة" },
                    { value: "widowed" as const, label: "أرمل/ة" },
                  ]).map(f => (
                    <Badge key={f.value} variant={maritalFilter === f.value ? "default" : "outline"} className={`cursor-pointer text-xs px-3 py-1.5 transition-colors ${maritalFilter === f.value ? '' : 'hover:bg-secondary/60'}`} onClick={() => setMaritalFilter(f.value)}>{f.label}</Badge>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {searchQuery && (
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {members.filter(m => m.name.includes(searchQuery)).length === 0 ? (
                <p className="text-muted-foreground text-xs">لا توجد نتائج</p>
              ) : (
                members.filter(m => m.name.includes(searchQuery)).map(m => (
                  <Badge
                    key={m.id}
                    variant="secondary"
                    className="cursor-pointer hover:bg-primary/20 transition-colors text-xs px-3 py-1.5"
                    onClick={() => {
                      setHighlightedId(m.id);
                      setSearchQuery("");
                      setTimeout(() => setHighlightedId(null), 3000);
                    }}
                  >
                    {m.name}
                  </Badge>
                ))
              )}
            </div>
          )}
        </motion.div>

        {/* Tree */}
        {loading ? (
          <div className="flex justify-center gap-10">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col items-center gap-3">
                <Skeleton className="h-20 w-20 rounded-full" />
                <Skeleton className="h-3 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <Card className="glass-card border-primary/10 max-w-md mx-auto rounded-2xl">
            <CardContent className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                <User className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">لم تتم إضافة أفراد العائلة بعد</p>
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            {/* Zoom controls */}
            <div className="sticky top-16 sm:top-20 z-10 flex flex-wrap justify-end mb-2 gap-0.5 sm:gap-1">
              <Button variant="outline" size="sm" className="h-7 sm:h-8 rounded-lg gap-1 sm:gap-1.5 text-[10px] sm:text-xs px-2 sm:px-3" onClick={allExpanded ? handleCollapseAll : handleExpandAll}>
                {allExpanded ? <Shrink className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : <Expand className="h-3 w-3 sm:h-3.5 sm:w-3.5" />}
                {allExpanded ? "طي" : "توسيع"}
              </Button>
              <Button variant="outline" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg" onClick={handleDownloadPng} disabled={isDownloading} title="تحميل كصورة">
                <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg" onClick={() => setZoom(z => Math.min(z + 0.15, 2))}>
                <ZoomIn className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg" onClick={() => setZoom(z => Math.max(z - 0.15, 0.3))}>
                <ZoomOut className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              {zoom !== 1 && (
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg" onClick={() => setZoom(1)}>
                  <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
              )}
              <span className="flex items-center text-[10px] sm:text-xs text-muted-foreground px-1">{Math.round(zoom * 100)}%</span>
            </div>
            <div ref={treeContainerRef} className={`overflow-auto pb-8 scrollbar-thin touch-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onClickCapture={(e) => { if (didDrag.current) { e.stopPropagation(); didDrag.current = false; } }}>
              <div ref={treeContentRef} className="inline-flex justify-center gap-6 sm:gap-8 md:gap-16 min-w-full py-2 sm:py-4 px-2 sm:px-4 md:px-8 origin-top transition-transform duration-200" style={{ transform: `scale(${zoom})` }}>
                {tree.map((node) => (
                  <TreeNodeComponent key={node.member.id} node={node} isAuthenticated={!!user} onSelect={setSelected} highlightedId={highlightedId} numberMap={memberNumberMap} expandedNodes={expandedNodes} onToggleExpand={handleToggleExpand} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <AnimatePresence>
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="glass-card border-primary/20 max-w-md rounded-2xl shadow-2xl" dir="rtl">
            <DialogHeader>
              <DialogTitle className="font-amiri text-xl gold-text">معلومات الفرد</DialogTitle>
            </DialogHeader>
            {selected && <MemberDetail member={selected} childrenCount={selectedCounts?.children} grandchildrenCount={selectedCounts?.grandchildren} memberNumber={memberNumberMap.get(selected.id)} memberCode={memberCodeMap.get(selected.id)} galleryPhotos={galleryPhotos.filter(p => p.member_id === selected.id)} memberBusinesses={memberBusinesses[selected.id] || []} onViewBusiness={(biz) => { setSelected(null); setTimeout(() => setViewingBusiness(biz), 200); }} />}
          </DialogContent>
        </Dialog>
      </AnimatePresence>

      {/* Business Detail Dialog */}
      <Dialog open={!!viewingBusiness} onOpenChange={() => setViewingBusiness(null)}>
        <DialogContent className="glass-card border-primary/20 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-amiri text-xl gold-text text-center">تفاصيل الشركة</DialogTitle>
          </DialogHeader>
          {viewingBusiness && (() => {
            const biz = viewingBusiness;
            const activities = biz.activity ? biz.activity.split(" | ").filter(a => a.trim()) : [];
            const contacts = [
              { icon: MapPinned, label: "الموقع على الخريطة", value: biz.website, href: biz.website, external: true },
              { icon: Phone, label: "رقم التواصل", value: biz.phone, href: `tel:${biz.phone}` },
              { icon: Mail, label: "البريد الإلكتروني", value: biz.email, href: `mailto:${biz.email}` },
              { icon: MessageCircle, label: "واتساب", value: biz.whatsapp, href: `https://wa.me/${biz.whatsapp?.replace(/[^0-9]/g, "")}`, external: true },
              { icon: Instagram, label: "انستغرام", value: biz.instagram, href: biz.instagram?.startsWith("http") ? biz.instagram : `https://instagram.com/${biz.instagram?.replace("@", "")}`, external: true },
              { icon: Facebook, label: "فيس بوك", value: biz.facebook, href: biz.facebook, external: true },
            ].filter(c => c.value);
            return (
              <div className="flex flex-col items-center gap-5 pt-2">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-secondary/30 flex items-center justify-center border-2 border-primary/30 shadow-lg ring-2 ring-primary/10 ring-offset-2 ring-offset-background">
                  <img src={biz.logo_url || defaultBusiness} alt={biz.name_ar} className={`w-full h-full ${biz.logo_url ? 'object-cover' : 'object-contain p-3'}`} />
                </div>
                <div className="text-center">
                  <h2 className="font-amiri text-2xl font-bold text-primary">{biz.name_ar}</h2>
                  {biz.name_en && <p className="text-muted-foreground mt-1">{biz.name_en}</p>}
                </div>
                {activities.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center">
                    {activities.map((a, i) => <Badge key={i} variant="secondary" className="text-xs">{a}</Badge>)}
                  </div>
                )}
                {(biz.country || biz.emirate || biz.area || biz.street) && (
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm">{[biz.country, biz.emirate, biz.area, biz.street].filter(Boolean).join(" - ")}</span>
                  </div>
                )}
                {biz.location && biz.location.startsWith("http") && (
                  <a href={biz.location} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
                    <MapPinned className="h-4 w-4" />
                    <span className="text-sm">عرض الموقع على الخريطة</span>
                  </a>
                )}
                {contacts.length > 0 && (
                  <div className="w-full pt-2 border-t border-primary/10">
                    <p className="text-xs text-muted-foreground text-center mb-3">وسائل التواصل</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {contacts.map((c, i) => (
                        <a key={i} href={c.href!} target={c.external ? "_blank" : undefined} rel={c.external ? "noopener noreferrer" : undefined}
                          className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-all group min-w-[70px]">
                          <div className="h-10 w-10 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-all">
                            <c.icon className="h-5 w-5 text-primary" />
                          </div>
                          <span className="text-xs text-primary font-medium">اضغط هنا</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyTree;
